import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  Box,
  LinearProgress,
  Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface ParsedPreview {
  title: string;
  description: string;
  sectionCount: number;
  promptCount: number;
  sections: string[];
}

interface ImportResult {
  projectId: string;
  projectName: string;
  packsCreated: number;
  promptsCreated: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
  importFn: (json: Record<string, unknown>) => Promise<{ data: ImportResult }>;
}

function parsePreview(text: string): { preview: ParsedPreview; json: Record<string, unknown> } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'Invalid JSON — paste a valid JSON object.' };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { error: 'Expected a JSON object at the top level.' };
  }

  const obj = parsed as Record<string, unknown>;

  if (!Array.isArray(obj.prompts) || obj.prompts.length === 0) {
    return { error: 'JSON must contain a non-empty "prompts" array.' };
  }

  const title = typeof obj.title === 'string' && obj.title.trim() ? obj.title.trim() : 'Imported Project';
  const description = typeof obj.description === 'string' ? obj.description.trim() : '';

  // Derive sections
  const sectionMap = new Map<number, string>();
  if (Array.isArray(obj.sections)) {
    for (const s of obj.sections as Array<{ number?: unknown; title?: unknown }>) {
      if (typeof s.number === 'number' && typeof s.title === 'string') {
        sectionMap.set(s.number, s.title.trim());
      }
    }
  }
  for (const p of obj.prompts as Array<{ section?: { number?: unknown; title?: unknown } }>) {
    const sec = p.section;
    if (sec && typeof sec.number === 'number' && !sectionMap.has(sec.number) && typeof sec.title === 'string') {
      sectionMap.set(sec.number, sec.title.trim());
    }
  }

  const sections = [...sectionMap.entries()].sort((a, b) => a[0] - b[0]).map(([, t]) => t);

  return {
    preview: {
      title,
      description,
      sectionCount: sections.length || 1,
      promptCount: obj.prompts.length,
      sections,
    },
    json: obj,
  };
}

export default function ImportProjectDialog({ open, onClose, onImported, importFn }: Props) {
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    setJsonText('');
    setError(null);
    onClose();
  };

  const parsed = jsonText.trim() ? parsePreview(jsonText.trim()) : null;
  const preview = parsed && 'preview' in parsed ? parsed.preview : null;
  const parseError = parsed && 'error' in parsed ? parsed.error : null;

  const handleImport = async () => {
    if (!parsed || !('json' in parsed)) return;
    setLoading(true);
    setError(null);
    try {
      const result = await importFn(parsed.json);
      onImported(result.data);
      handleClose();
    } catch (err: unknown) {
      // Prefer the server's `error.message` (and field details if present) over the
      // generic axios "Request failed with status code 400".
      let msg = 'Import failed';
      if (err && typeof err === 'object') {
        const e = err as {
          response?: { data?: { error?: { message?: string; details?: unknown } } };
          message?: string;
        };
        const serverErr = e.response?.data?.error;
        if (serverErr?.message) {
          msg = serverErr.message;
          if (serverErr.details && typeof serverErr.details === 'object') {
            const detailEntries = Object.entries(serverErr.details as Record<string, unknown>)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
              .join(' | ');
            if (detailEntries) msg = `${msg} — ${detailEntries}`;
          }
        } else if (e.message) {
          msg = e.message;
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon fontSize="small" />
        Import Project from JSON
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a JSON prompt pack (AgentShield format). Each section becomes a Pack; all packs are
          grouped into a new Project.
        </Typography>

        {(error || parseError) && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error || parseError}
          </Alert>
        )}

        {preview && !parseError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {preview.title}
            </Typography>
            {preview.description && (
              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                {preview.description.length > 200
                  ? `${preview.description.slice(0, 200)}…`
                  : preview.description}
              </Typography>
            )}
            <Typography variant="body2">
              {preview.sectionCount} section{preview.sectionCount !== 1 ? 's' : ''} →{' '}
              {preview.promptCount} prompt{preview.promptCount !== 1 ? 's' : ''}
            </Typography>
            {preview.sections.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {preview.sections.slice(0, 10).map((s) => (
                  <Chip key={s} label={s} size="small" variant="outlined" />
                ))}
                {preview.sections.length > 10 && (
                  <Chip label={`+${preview.sections.length - 10} more`} size="small" />
                )}
              </Box>
            )}
          </Alert>
        )}

        <TextField
          label="JSON"
          multiline
          minRows={10}
          maxRows={20}
          fullWidth
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          disabled={loading}
          placeholder='{ "title": "My Prompt Pack", "sections": [...], "prompts": [...] }'
          error={!!parseError}
          spellCheck={false}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !preview || !!parseError}
          startIcon={<UploadFileIcon />}
        >
          {loading
            ? 'Importing…'
            : preview
            ? `Import ${preview.promptCount} Prompt${preview.promptCount !== 1 ? 's' : ''} into Project`
            : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
